import React from "react";
import { Logo } from "~/components/icons";
import type { SpaceMetadataFragment } from "~/graphql/codegen/graphql";
import { classNames } from "~/utils";

type SpaceLogoVariant = "full" | "bar" | "rail";

interface Props {
  /** The current space's partner, if any. When null/undefined the Mindstone logo renders unchanged. */
  partner: SpaceMetadataFragment["partner"];
  /**
   * Which chrome slot this logo sits in (each has different space constraints):
   * - "full" (default): expanded sidebar / mobile menu sheet (wide + tall) —
   *   partner chip with "powered by <Mindstone wordmark>" stacked beneath.
   * - "bar": mobile top bar (wide but only ~32px tall) — single line, compact +
   *   shrinkable so it never overflows the bar width.
   * - "rail": collapsed ~64px desktop icon rail (narrow but tall) — stacked, with
   *   a width-fitting "powered by Mindstone" (wordmark is too wide, so text).
   */
  variant?: SpaceLogoVariant;
  /** Only used for the non-partner Mindstone <Logo/> fallback (byte-identical to today). */
  className?: string;
}

/**
 * Space chrome logo. Encapsulates the partner-vs-Mindstone branch so the four
 * app-layout call sites stay DRY. No partner → the existing Mindstone <Logo/>
 * exactly as today. Partner set → partner logo plus a "powered by Mindstone"
 * attribution sized to whatever slot it sits in. The expanded sidebar/mobile
 * sheet variant sits directly on the sidebar surface; the collapsed icon rail
 * keeps a small light chip for contrast against the narrow slot.
 */
export function SpaceLogo({ partner, variant = "full", className }: Props) {
  if (!partner) {
    // Non-partner: preserve today's Mindstone logo. bar + rail collapse to the icon.
    return <Logo className={className} iconOnly={variant !== "full"} />;
  }

  const chip = (extra: string) => (
    <span className={classNames("inline-flex items-center justify-center rounded-md", extra)}>
      <img src={partner.logo} alt={partner.name} className="w-full h-auto max-h-16 object-contain" />
    </span>
  );

  const showAttribution = partner.showMindstoneLogo ?? true;

  // Mobile top bar: too cramped for partner branding alongside the hamburger +
  // action icons (lightning / chart / notifications / avatar). Omit it here —
  // the partner logo + "powered by Mindstone" still shows in the mobile menu
  // drawer (the "full" variant) and the desktop sidebar.
  if (variant === "bar") {
    return null;
  }

  // Collapsed icon rail: no horizontal room for the wordmark → stack, and render
  // "powered by Mindstone" as tiny text (the wordmark image won't fit ~64px).
  if (variant === "rail") {
    return (
      <span className="inline-flex max-w-full flex-col items-center gap-1">
        {chip("max-w-12 p-1 bg-white/95")}
        {showAttribution && (
          <span className="flex flex-col items-center gap-0.5 text-center leading-tight text-muted-foreground">
            <span className="text-[9px]">powered by</span>
            <span className="text-[10px] font-semibold text-foreground">Mindstone</span>
          </span>
        )}
      </span>
    );
  }

  // Expanded sidebar / mobile sheet: stacked, full wordmark.
  // No background chip — the logo sits directly on the sidebar surface so it
  // lines up cleanly with the nav items and avoids the "gray box" effect.
  return (
    <span className="flex w-full flex-col gap-1">
      {chip("w-full")}
      {showAttribution && (
        <span className="flex items-center gap-1 whitespace-nowrap text-[10px] leading-none text-muted-foreground">
          powered by
          <Logo className="h-3 w-auto" />
          <span className="sr-only">Mindstone</span>
        </span>
      )}
    </span>
  );
}

export default SpaceLogo;
